/** Bump when a question or option changes meaning; stored answers carry the version they used. */
export const PLACEMENT_QUESTIONNAIRE_VERSION = 1;
/** The one source for the questions the browser renders and the server validates. */
export const PLACEMENT_QUESTIONS = [
    {
        id: 'coding',
        prompt: 'How much programming have you done?',
        options: [
            { value: 'none', label: 'None, or a few tutorials' },
            { value: 'some', label: 'Some: scripts, coursework or side projects' },
            { value: 'professional', label: 'Professional: I have written code for a living' },
        ],
    },
    {
        id: 'webApp',
        prompt: 'Have you built a web application with a database, an HTTP API and a browser UI?',
        options: [
            { value: 'no', label: 'No' },
            { value: 'parts', label: 'Parts of one, or with a lot of help' },
            { value: 'endToEnd', label: 'Yes, end to end' },
        ],
    },
    {
        id: 'frontend',
        prompt: 'How comfortable are you with React and TypeScript, including tests?',
        options: [
            { value: 'none', label: 'I have not used them' },
            { value: 'some', label: 'I can read and change a React page' },
            { value: 'confident', label: 'Confident: I build and test typed React clients' },
        ],
    },
    {
        id: 'llm',
        prompt: 'Have you called a language-model API from your own code?',
        options: [
            { value: 'no', label: 'No' },
            {
                value: 'basic',
                label: 'Yes, but without checking its output against a schema or an evaluation set',
            },
            { value: 'validated', label: 'Yes, with validated output and an evaluation set' },
        ],
    },
    {
        id: 'agents',
        prompt: 'Have you built agents or workflows: tool use, retrieval, state, approvals?',
        options: [
            { value: 'no', label: 'No' },
            { value: 'prototypes', label: 'Prototypes or demos' },
            { value: 'shipped', label: 'Yes, in production, with approvals and reliability measurement' },
        ],
    },
    {
        id: 'goal',
        prompt: 'What do you most want from this program?',
        options: [
            { value: 'foundations', label: 'Solid programming and web foundations' },
            { value: 'fullStack', label: 'Build complete web applications' },
            { value: 'aiFeatures', label: 'Add language-model features to applications' },
            { value: 'agents', label: 'Build agents and AI workflows' },
        ],
    },
];
function ruleFor(number, answers, title) {
    switch (number) {
        case 1: {
            if (answers.coding !== 'professional')
                return {
                    recommended: true,
                    reason: `You have not programmed professionally yet, so ${title} starts where the application starts: one Python instruction at a time, up to a browser page.`,
                };
            if (answers.webApp === 'no')
                return {
                    recommended: true,
                    reason: `You have not built a web application end to end, so ${title} gives you the terminal-to-browser build first.`,
                };
            return {
                recommended: false,
                reason: `You already program professionally and have built web applications; ${title} would be a refresher of Python, files, HTTP and a browser page.`,
            };
        }
        case 2: {
            if (answers.webApp !== 'endToEnd')
                return {
                    recommended: true,
                    reason: `You have not shipped a web application end to end, so ${title} covers the database, related records, a typed React client, sign-in and permissions.`,
                };
            if (answers.frontend !== 'confident')
                return {
                    recommended: true,
                    reason: `React and TypeScript with tests are not yet comfortable, so ${title} builds the typed client beside the database and permission work.`,
                };
            return {
                recommended: false,
                reason: `You have shipped full-stack applications with React and TypeScript; ${title} would be a refresher of database, client and permission work.`,
            };
        }
        case 3: {
            if (answers.llm === 'no')
                return {
                    recommended: true,
                    reason: `You have not called a language-model API from code, so ${title} adds the first model feature and measures it.`,
                };
            if (answers.llm === 'basic')
                return {
                    recommended: true,
                    reason: `You have called a model but not held its output to a schema or measured it, so ${title} adds validation, evaluation, streaming and cost control.`,
                };
            return {
                recommended: false,
                reason: `You already validate model output and run evaluations; ${title} would be a refresher.`,
            };
        }
        case 4: {
            if (answers.agents === 'no')
                return {
                    recommended: true,
                    reason: `You have not built agents or workflows, so ${title} turns a model feature into an assistant with tools, retrieval, state and approval.`,
                };
            if (answers.agents === 'prototypes')
                return {
                    recommended: true,
                    reason: `You have prototyped agents, so ${title} adds what demos skip: approval, permission filters and reliability measurement.`,
                };
            return {
                recommended: false,
                reason: `You have shipped agent systems with approvals and reliability measurement; ${title} would be a refresher.`,
            };
        }
        default:
            return null;
    }
}
const GOAL_NOTE = {
    foundations: 'Your goal is solid foundations, so the earlier modules carry the most weight.',
    fullStack: 'Your goal is complete web applications, so the application-building modules matter most.',
    aiFeatures: 'Your goal is model features inside applications, so the path leads to AI Fundamentals.',
    agents: 'Your goal is agents, so the path leads to AI Agents and Workflows.',
};
/**
 * Maps answers to a recommended path over the modules the program publishes. Every module is
 * reasoned about by title so the result reads the same in the dialog, in Studio and to Mio.
 */
export function recommendPlacement(answers, modules) {
    const ordered = [...modules].sort((a, b) => a.number - b.number);
    const rationale = [];
    for (const module of ordered) {
        const rule = ruleFor(module.number, answers, module.title);
        if (!rule)
            continue;
        rationale.push({ module: module.number, title: module.title, ...rule });
    }
    let recommended = rationale.filter((item) => item.recommended).map((item) => item.module);
    const capstone = ordered.at(-1);
    if (recommended.length === 0 && capstone) {
        recommended = [capstone.number];
        const entry = rationale.find((item) => item.module === capstone.number);
        if (entry) {
            entry.recommended = true;
            entry.reason = `Every module would be a refresher for you; ${capstone.title} is the most demanding, so take it as a capstone.`;
        }
    }
    const startModule = recommended[0] ?? ordered[0]?.number ?? 1;
    const titleOf = (number) => ordered.find((m) => m.number === number)?.title ?? `Module ${number}`;
    const rest = recommended.slice(1).map(titleOf);
    const summary = [
        `Start with ${titleOf(startModule)}.`,
        rest.length > 0 ? `Then take ${rest.join(', ')}.` : 'That is the whole path.',
        GOAL_NOTE[answers.goal],
    ].join(' ');
    return { startModule, modules: recommended, rationale, summary };
}
/** Accepts the recommendation as is, or the learner's adjustment when it names published modules. */
export function validateChosenPath(recommendation, chosen, available) {
    const modules = [...new Set(chosen.modules ?? recommendation.modules)].sort((a, b) => a - b);
    if (modules.length === 0)
        return { ok: false, reason: 'A path needs at least one module.' };
    const unknown = modules.find((number) => !available.includes(number));
    if (unknown !== undefined)
        return { ok: false, reason: `Module ${unknown} is not part of this program.` };
    const startModule = chosen.startModule ?? modules[0] ?? recommendation.startModule;
    if (!modules.includes(startModule))
        return { ok: false, reason: 'The starting module must be one of the chosen modules.' };
    const same = startModule === recommendation.startModule &&
        modules.length === recommendation.modules.length &&
        modules.every((number, index) => number === recommendation.modules[index]);
    return { ok: true, path: { startModule, modules, source: same ? 'recommended' : 'adjusted' } };
}
/**
 * Module numbers in the order "next activity" should search them: the path's modules first,
 * in curriculum order, then everything else. Without a path, curriculum order is unchanged.
 */
export function pathOrderedModuleNumbers(path, all) {
    if (!path)
        return [...all];
    const chosen = new Set(path.modules);
    return [
        ...all.filter((number) => chosen.has(number)),
        ...all.filter((number) => !chosen.has(number)),
    ];
}
/** One line about the learner's background for Mio's context; never shown as a label. */
export function placementBackgroundSummary(answers) {
    const label = (id) => PLACEMENT_QUESTIONS.find((question) => question.id === id)?.options.find((option) => option.value === answers[id])?.label ?? String(answers[id]);
    return [
        `Programming: ${label('coding')}.`,
        `Web applications: ${label('webApp')}.`,
        `React and TypeScript: ${label('frontend')}.`,
        `Language-model APIs: ${label('llm')}.`,
        `Agents: ${label('agents')}.`,
        `Goal: ${label('goal')}.`,
    ].join(' ');
}
